import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { useGetImagesPaginated } from "../../api/queries";
import { useDeleteImage } from "../../api/mutations";
import { ImageI, ImageTypeEnum } from "../../types";
import { useNotification } from "../../hooks/useNotification";
import { usePermissions } from "../../contexts/PermissionContext";
import { PermissionEnum } from "../../types/rbac";
import Button from "../../componentLibrary/Button";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import AddImageModal from "../../components/AddImageModal";
import Spinner from "../../componentLibrary/Spinner";
import CustomTabGroup from "../../componentLibrary/TabGroup";
import Toolbar from "./components/Toolbar";
import PaginationSettings from "./components/PaginationSettings";
import ImageShowcase from "./components/ImageShowcase";
import EditImageModal from "./components/EditImageModal";

export type ImageExtension =
  | ".png"
  | ".jpg"
  | ".jpeg"
  | ".gif"
  | ".svg"
  | ".webp";

function IconsIllustrationsTab({
  canCreateContent,
  canUpdateContent,
  canDeleteContent,
  singleType,
}: {
  canCreateContent: boolean;
  canUpdateContent: boolean;
  canDeleteContent: boolean;
  /** When set, this tab shows only one image type (Icons or Illustrations); no type dropdown. */
  singleType?: ImageTypeEnum;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<ImageTypeEnum>(
    singleType ?? ImageTypeEnum.ICON,
  );
  const [extensionFilter, setExtensionFilter] = useState<ImageExtension>(null);
  const [selectedImage, setSelectedImage] = useState<ImageI | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const effectiveType = singleType ?? typeFilter;
  const [addImageType, setAddImageType] =
    useState<ImageTypeEnum>(effectiveType);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { notify } = useNotification();

  const { data, isLoading, error } = useGetImagesPaginated(
    effectiveType,
    searchTerm,
    page,
    limit,
    extensionFilter,
  );
  const images = data?.data || [];
  const meta = data?.meta;

  React.useEffect(() => {
    setPage(1);
  }, [effectiveType, searchTerm, limit]);

  const deleteMutation = useDeleteImage({
    onSuccess: async () => {
      notify("success", "Image deleted successfully");
      setDeleteConfirmOpen(false);
      setSelectedImage(null);
      await queryClient.invalidateQueries({ queryKey: ["images"] });
    },
    onError: () => {
      notify("error", "Failed to delete image. You may not have permission.");
    },
  });

  const handleEditClick = (image: ImageI) => {
    setSelectedImage(image);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (image: ImageI) => {
    setSelectedImage(image);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedImage) {
      deleteMutation.mutate(selectedImage._id!);
    }
  };

  const handleAddImage = (type: ImageTypeEnum) => {
    setAddImageType(type);
    setAddModalOpen(true);
  };

  const showEmptyAddButtons =
    images.length === 0 &&
    (singleType
      ? !searchTerm && !extensionFilter
      : !searchTerm && typeFilter === ImageTypeEnum.ICON);

  if (error) return <div>Error Loading Images</div>;

  return (
    <>
      <Toolbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        extensionFilter={extensionFilter}
        setExtensionFilter={setExtensionFilter}
        handleAddImage={handleAddImage}
        setPage={setPage}
        setLimit={setLimit}
        canCreateContent={canCreateContent}
        singleType={singleType}
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <PaginationSettings
            page={page}
            limit={limit}
            images={images}
            meta={meta}
            setPage={setPage}
            setLimit={setLimit}
          />

          {/* Images grid */}
          {images.length === 0 ? (
            <div className="text-center py-12">
              <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No images found
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || extensionFilter
                  ? "Try adjusting your search or filters"
                  : `Get started by uploading your first ${effectiveType.toLowerCase()}`}
              </p>
              {showEmptyAddButtons && (
                <div className="flex justify-center gap-2">
                  {singleType ? (
                    <Button
                      variant={
                        effectiveType === ImageTypeEnum.ILLUSTRATION
                          ? "primary"
                          : "secondary"
                      }
                      onClick={() =>
                        canCreateContent && handleAddImage(effectiveType)
                      }
                      disabled={!canCreateContent}
                      title={
                        !canCreateContent
                          ? "Ask an admin if you need permission to add images"
                          : `Add ${effectiveType}`
                      }
                    >
                      Add {effectiveType}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          canCreateContent && handleAddImage(ImageTypeEnum.ICON)
                        }
                        disabled={!canCreateContent}
                        title={
                          !canCreateContent
                            ? "Ask an admin if you need permission to add images"
                            : "Add Icon"
                        }
                      >
                        Add Icon
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() =>
                          canCreateContent &&
                          handleAddImage(ImageTypeEnum.ILLUSTRATION)
                        }
                        disabled={!canCreateContent}
                        title={
                          !canCreateContent
                            ? "Ask an admin if you need permission to add images"
                            : "Add Illustration"
                        }
                      >
                        Add Illustration
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {images.map((image) => (
                <ImageShowcase
                  key={image._id}
                  image={image}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  canUpdateContent={canUpdateContent}
                  canDeleteContent={canDeleteContent}
                />
              ))}
            </div>
          )}
        </>
      )}

      {selectedImage && (
        <EditImageModal
          isModalOpen={editModalOpen}
          setIsModalOpen={setEditModalOpen}
          setSelectedImage={setSelectedImage}
          selectedImage={selectedImage}
        />
      )}

      <ConfirmationBox
        isOpen={deleteConfirmOpen}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setSelectedImage(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Image"
        message={
          selectedImage
            ? `Are you sure you want to delete "${selectedImage.name}"? This action cannot be undone.`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
      />

      <AddImageModal
        isModalOpen={addModalOpen}
        setIsModalOpen={setAddModalOpen}
        type={addImageType}
      />
    </>
  );
}

export default function ImageManagementPage() {
  const { hasPermission } = usePermissions();

  const canCreateContent = hasPermission([
    PermissionEnum.CONTENT_CREATE,
    PermissionEnum.CONTENT_ALL,
  ]);
  const canUpdateContent = hasPermission([
    PermissionEnum.CONTENT_UPDATE,
    PermissionEnum.CONTENT_ALL,
  ]);
  const canDeleteContent = hasPermission([
    PermissionEnum.CONTENT_DELETE,
    PermissionEnum.CONTENT_ALL,
  ]);

  const tabs = [
    {
      label: "Icons",
      content: (
        <div className="pt-6">
          <IconsIllustrationsTab
            canCreateContent={canCreateContent}
            canUpdateContent={canUpdateContent}
            canDeleteContent={canDeleteContent}
            singleType={ImageTypeEnum.ICON}
          />
        </div>
      ),
    },
    {
      label: "Illustrations",
      content: (
        <div className="pt-6">
          <IconsIllustrationsTab
            canCreateContent={canCreateContent}
            canUpdateContent={canUpdateContent}
            canDeleteContent={canDeleteContent}
            singleType={ImageTypeEnum.ILLUSTRATION}
          />
        </div>
      ),
    },
    {
      label: "Thumbnails",
      content: (
        <div className="pt-6">
          <IconsIllustrationsTab
            canCreateContent={canCreateContent}
            canUpdateContent={canUpdateContent}
            canDeleteContent={canDeleteContent}
            singleType={ImageTypeEnum.THUMBNAIL}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Image Dashboard
        </h1>
        <p className="text-gray-600">
          Manage all your images: view, search, edit, and delete images in your
          system
        </p>
      </div>

      <CustomTabGroup tabs={tabs} variant="underline" />
    </div>
  );
}
